/* eslint-disable no-underscore-dangle */
import { useEffect, useState } from 'react'

import { useLazyQuery, useQuery } from '@apollo/client'
import { graphql } from '@apollo/client/react/hoc'
import { useNotifications } from '@joor/design-system'
import get from 'lodash/get'

import {
  OrderExportTaskStatus,
  OrderExportTaskStatusDocument,
} from '__generated__/atlas-types'

import { download } from 'utils'
import { appliedFiltersToGraphQLVariables } from 'utils/filters/utils'
import {
  addDivisionsToOrders,
  getOrderExportIntegrations,
  getOrders,
} from 'utils/transformations'
import {
  relayConnectionToArray,
  relayConnectionToArrayDeep,
  toGlobalId,
} from 'utils/transformations/graphql'
import { getCollectionNames, getLineItems } from 'utils/transformations/orders'

import { atlasClient } from 'graphql/client'
import { atlas, graphene } from 'graphql/tags'
import { useRenderToasters } from 'hooks/useRenderToasters'

import { MOVE_PRODUCTS_PO_SELECTION } from 'routes/paths'

import { HELP_LINK } from '../../features/Orders/MergeOrders/mergeOrders.constants'
import styles from './OrderList.component.module.css'
import { orderEvents, orders, salesReps } from './OrderList.fragments'
import { orderFilters } from 'containers/OrderListFilters/filters'

export const OrdersDivisions = graphene`#graphql
  query OrdersDivisions(
    $pageSize: Int
    $after: String
    $appliedFilters: OrderFilterInput
    $searchFilter: String
    $orderBy: [OrderOrderByInput]
  ) {
    viewerBrand {
      id
      orders(
        first: $pageSize
        after: $after
        filterBy: $appliedFilters
        searchText: $searchFilter
        orderBy: $orderBy
      ) {
        edges {
          node {
            id
            divisions {
              edges {
                node {
                  id
                  code
                }
              }
            }
          }
        }
        pageInfo {
          endCursor
        }
      }
    }
  }
`

export const Orders = graphene`#graphql
  query orders(
    $pageSize: Int
    $after: String
    $appliedFilters: OrderFilterInput
    $searchFilter: String
    $orderBy: [OrderOrderByInput]
    $excludeInProgress: Boolean
  ) {
    viewerBrand {
      id
      ...OrderEvents
      ...SalesReps
      orders(
        first: $pageSize
        after: $after
        filterBy: $appliedFilters
        searchText: $searchFilter
        orderBy: $orderBy
        excludeInProgress: $excludeInProgress
      ) {
        edges {
          node {
            id
            webOrderLookupId
            brand {
              id
              name
            }
            retailer {
              id
              email
            }
            retailerName
            billingFirstAddress
            billingSecondAddress
            billingCountry
            billingCity
            billingZip
            shippingAddress {
              id
              code
            }
            shippingEmail
            status
            latestExports {
              edges {
                node {
                  id
                  name
                  status
                  exportDescription
                  created
                }
              }
            }
            referenceNumber
            collections {
              edges {
                node {
                  id
                  name
                }
              }
            }
            modified
            deliveryComplete
            eventName
            eventCode
            salesRepresentative {
              id
              name
              code
            }
            type {
              edges {
                node {
                  id
                  name
                  code
                }
              }
            }
            budgets {
              edges {
                node {
                  id
                  wholesaleTotal
                }
              }
            }
            webOrderLookupId
            priceType {
              id
              name
              currency {
                id
                code
              }
            }
            lineItems {
              totalUnits
              edges {
                node {
                  id
                  quantity
                }
              }
            }
            financials {
              dueTotal {
                maxPrice
              }
            }
            hasEmptyStyleColors
            webOrderLookupId
            createdByRetailer
            createdAt
            isUnassigned
            warehouse {
              id
              name
              code
            }
            orphanRetailer {
              id
              retailerEmail
              retailerName
            }
          }
        }
        pageInfo {
          endCursor
        }
      }
      orderExportIntegrations {
        edges {
          node {
            id
            isManuallyScheduled
            application {
              id
              name
              integrationType
            }
          }
        }
      }
      budgets {
        totalCount
      }
    }
  }
  ${salesReps}
  ${orderEvents}
`

export const RetailerOrdersQuery = graphene`#graphql
  query retailerOrdersQuery(
    $pageSize: Int
    $after: String
    $appliedFilters: RetailerOrdersFilterInput
    $searchFilter: String
    $orderBy: [OrderOrderByInput]
  ) {
    viewer {
      id
      viewerAccount {
        ... on RetailerAccount {
          id
          orders(
            first: $pageSize
            after: $after
            filterBy: $appliedFilters
            searchText: $searchFilter
            orderBy: $orderBy
          ) {
            pageInfo {
              endCursor
            }
            edges {
              node {
                id
                webOrderLookupId
                referenceNumber
                status
                hasEmptyStyleColors
                deliveryComplete
                modified
                brand {
                  id
                  name
                }
                lineItems {
                  totalUnits
                  edges {
                    node {
                      id
                      quantity
                    }
                  }
                }
                financials {
                  dueTotal {
                    maxPrice
                  }
                }
                priceType {
                  id
                  name
                  currency {
                    id
                    code
                  }
                }
                shippingName
                collections {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const SalesReps = graphene`#graphql
  query ViewerBrand {
    viewerBrand {
      ...SalesReps,
      ...Orders,
    }
  }
  ${salesReps},
  ${orders}
`

export const OrderEvents = graphene`#graphql
  query ViewerBrand {
    viewerBrand {
      ...OrderEvents,
      ...Orders,
    }
  }
  ${orderEvents},
  ${orders}
`

export const OrdersTotals = graphene`#graphql
  query ordersTotals($appliedFilters: OrderFilterInput, $searchFilter: String) {
    viewerBrand {
      id
      orders(filterBy: $appliedFilters, searchText: $searchFilter) {
        totalCount
      }
    }
  }
`

export const RetailerOrdersTotalsQuery = graphene`#graphql
  query retailerOrdersTotalsQuery(
    $appliedFilters: RetailerOrdersFilterInput
    $searchFilter: String
  ) {
    viewer {
      id
      viewerAccount {
        ... on RetailerAccount {
          id
          orders(filterBy: $appliedFilters, searchText: $searchFilter) {
            totalCount
          }
        }
      }
    }
  }
`

export const OrdersStaticCounts = graphene`#graphql
  query ordersStaticCounts($excludeInProgress: Boolean) {
    viewerBrand {
      id
      ordersCancelled: orders(filterBy: { status: [CANCELLED] }) {
        totalCount
      }
      ordersApproved: orders(filterBy: { status: [APPROVED] }) {
        totalCount
      }
      ordersShipped: orders(filterBy: { status: [SHIPPED] }) {
        totalCount
      }
      ordersWithQuantities: orders(
        filterBy: { status: NOTES_WITH_QUANTITIES }
      ) {
        totalCount
      }
      ordersWithoutQuantities: orders(
        filterBy: { status: NOTES_WITHOUT_QUANTITIES }
      ) {
        totalCount
      }
      ordersPending: orders(filterBy: { status: [PENDING] }) {
        totalCount
      }
      ordersDraft: orders(filterBy: { status: [IN_PROGRESS] }, excludeInProgress: $excludeInProgress){
        totalCount
      }
    }
  }
`

export const MergingCriteria = atlas`#graphql
  query checkMergingOrderCriteriaDupe1($selectedOrdersIds: [ID!]!) {
    checkMergingOrderCriteria(orderIds: $selectedOrdersIds) {
      message
      success
    }
  }
`

export const OrderSettings = atlas`#graphql
  query OrderSettingsDupe1($brandIds: [ID!]) {
    orderSettings(brandIds: $brandIds) {
      multipleDivisionsAllowed
    }
  }
`

export const RetailerOrdersStaticCounts = graphene`#graphql
  query retailerOrdersStaticCounts {
    viewer {
      id
      viewerAccount {
        ... on RetailerAccount {
          id
          ordersCancelled: orders(filterBy: { status: [CANCELLED] }) {
            totalCount
          }
          ordersApproved: orders(filterBy: { status: [APPROVED] }) {
            totalCount
          }
          ordersShipped: orders(filterBy: { status: [SHIPPED] }) {
            totalCount
          }
          ordersWithQuantities: orders(
            filterBy: { status: NOTES_WITH_QUANTITIES }
          ) {
            totalCount
          }
          ordersWithoutQuantities: orders(
            filterBy: { status: NOTES_WITHOUT_QUANTITIES }
          ) {
            totalCount
          }
          ordersPending: orders(filterBy: { status: [PENDING] }) {
            totalCount
          }
          ordersDraft: orders(filterBy: { status: [IN_PROGRESS] }) {
            totalCount
          }
        }
      }
    }
  }
`

const loadMoreOrdersFn =
  ({ query, viewerBrand, fetchMore, appliedFilters, searchFilter, orderBy }) =>
  (pageSize) =>
    fetchMore({
      query,
      variables: {
        after: get(viewerBrand, 'orders.pageInfo.endCursor'),
        pageSize,
        appliedFilters: appliedFiltersToGraphQLVariables(
          orderFilters,
          appliedFilters,
        ),
        searchFilter,
        orderBy,
      },
      updateQuery: (_, { fetchMoreResult }) => {
        // we can't use previous result here because when getting division codes and
        // orders at the same time apollo does weird stuff with previousResult
        // but since we were passing viewerBrand already and it is equivalent
        // to previousResult, we can use that
        const newEdges = get(fetchMoreResult, 'viewerBrand.orders.edges')

        return newEdges.length
          ? {
              viewerBrand: {
                ...viewerBrand,
                __typename: viewerBrand.__typename,
                orders: {
                  __typename: get(viewerBrand, 'orders.__typename'),
                  edges: [...get(viewerBrand, 'orders.edges', []), ...newEdges],
                  pageInfo: get(fetchMoreResult, 'viewerBrand.orders.pageInfo'),
                },
              },
            }
          : viewerBrand
      },
    })

/*
 * withOrdersDivisions is a HOC that uses the OrdersDivisions query.
 * The point of this HOC is to be above withOrders, to fetch the divisions data.
 * This is done because the division data is slow,
 * but we can start rendering data received in the first query.
 */
export const withOrdersDivisions = (WrappedComponent) => (props) => {
  const {
    appliedFilters,
    searchFilter,
    orderBy,
    pageSize,
    shouldDisplayDivisionColumn,
    ordersQueryLoading,
    ordersQueryError,
    orders: ordersWithoutDivisions,
    loadMoreEntries: loadMoreOrdersWithoutDivisions,
  } = props
  const { loading, data, error, refetch, fetchMore } = useQuery(
    OrdersDivisions,
    {
      variables: {
        pageSize,
        after: '',
        appliedFilters: appliedFiltersToGraphQLVariables(
          orderFilters,
          appliedFilters,
        ),
        searchFilter,
        orderBy,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !shouldDisplayDivisionColumn,
    },
  )

  const customProps = {
    // when this query and the orders query finished loading without errors
    // add the divisions to the list of orders without division
    ...props,
    orders:
      !loading &&
      !ordersQueryLoading &&
      !error &&
      !ordersQueryError &&
      data?.viewerBrand
        ? addDivisionsToOrders(
            getOrders(data?.viewerBrand.orders),
            ordersWithoutDivisions,
          )
        : ordersWithoutDivisions,
    ordersQueryError: error || ordersQueryError,
    orderDivisionsLoading: loading,
    refetchOrdersDivisions: refetch,
    loadMoreEntries: (pageSize) => {
      loadMoreOrdersWithoutDivisions(pageSize)
      loadMoreOrdersFn({
        query: OrdersDivisions,
        viewerBrand: data?.viewerBrand,
        fetchMore,
        appliedFilters,
        searchFilter,
        orderBy,
      })(pageSize)
    },
  }

  return <WrappedComponent {...customProps} />
}

export const withOrders = graphql(Orders, {
  options: ({ appliedFilters, searchFilter, orderBy, pageSize }) => ({
    variables: {
      pageSize: pageSize,
      after: '',
      appliedFilters: appliedFiltersToGraphQLVariables(
        orderFilters,
        appliedFilters,
      ),
      searchFilter,
      orderBy,
      excludeInProgress: false,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  }),
  props: ({
    data: { loading, viewerBrand, error, refetch, fetchMore },
    ownProps: { appliedFilters, searchFilter, orderBy },
  }) => ({
    ordersQueryLoading: loading,
    budgetsTotalCount:
      !loading && !error ? get(viewerBrand, 'budgets.totalCount', 0) : 0,
    orderExportIntegrations: viewerBrand
      ? getOrderExportIntegrations(viewerBrand)
      : [],
    orders:
      !loading && !error && viewerBrand ? getOrders(viewerBrand.orders) : [],
    orderEvents:
      !loading && !error && viewerBrand
        ? relayConnectionToArrayDeep(viewerBrand.orderEvents)
        : [],
    salesReps:
      !loading && !error && viewerBrand
        ? relayConnectionToArrayDeep(viewerBrand.salesRepresentatives)
        : [],
    ordersQueryError: error,
    refetchOrdersOnly: refetch,
    loadMoreEntries: loadMoreOrdersFn({
      query: Orders,
      viewerBrand,
      fetchMore,
      appliedFilters,
      searchFilter,
      orderBy,
    }),
  }),
})

export const withOrdersTotals = graphql(OrdersTotals, {
  options: ({ appliedFilters, searchFilter }) => ({
    variables: {
      appliedFilters: appliedFiltersToGraphQLVariables(
        orderFilters,
        appliedFilters,
      ),
      searchFilter,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  }),
  props: ({
    data: { loading, viewerBrand, error, refetch },
    ownProps: { refetchOrdersOnly, refetchOrdersDivisions },
  }) => ({
    ordersTotalsQueryLoading: loading,
    totalCount:
      !loading && !error ? get(viewerBrand, 'orders.totalCount', 0) : 0,
    ordersTotalsQueryError: error,
    refetchOrders: () => {
      refetch()
      refetchOrdersOnly()
      refetchOrdersDivisions()
    },
  }),
})

export const withOrdersStaticCounts = graphql(OrdersStaticCounts, {
  options: () => ({
    variables: {
      excludeInProgress: false,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  }),
  props: ({ data: { loading, viewerBrand, error, refetch } }) => ({
    ordersCancelled:
      !loading && !error
        ? get(viewerBrand, 'ordersCancelled.totalCount', null)
        : null,
    ordersApproved:
      !loading && !error
        ? get(viewerBrand, 'ordersApproved.totalCount', null)
        : null,
    ordersShipped:
      !loading && !error
        ? get(viewerBrand, 'ordersShipped.totalCount', null)
        : null,
    ordersWithQuantities:
      !loading && !error
        ? get(viewerBrand, 'ordersWithQuantities.totalCount', null)
        : null,
    ordersWithoutQuantities:
      !loading && !error
        ? get(viewerBrand, 'ordersWithoutQuantities.totalCount', null)
        : null,
    ordersPending:
      !loading && !error
        ? get(viewerBrand, 'ordersPending.totalCount', null)
        : null,
    ordersDraft:
      !loading && !error
        ? get(viewerBrand, 'ordersDraft.totalCount', null)
        : null,
    ordersStaticCountsQueryError: error,
    refetchStaticCounts: refetch,
  }),
})

export const withRetailerOrders = (WrappedComponent) => (props) => {
  const { searchFilter, pageSize, appliedFilters, orderBy } = props
  const { loading, data, error, refetch, fetchMore } = useQuery(
    RetailerOrdersQuery,
    {
      variables: {
        pageSize: pageSize,
        after: '',
        appliedFilters: {
          ...appliedFiltersToGraphQLVariables(orderFilters, appliedFilters),
        },
        searchFilter,
        orderBy,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  )
  const formatRetailerOrders = (ordersFromQuery) => {
    const orders = relayConnectionToArray(ordersFromQuery)
    return orders.map((order) => ({
      ...order,
      lineItems: getLineItems(order.lineItems),
      collectionNames: getCollectionNames(order),
    }))
  }

  const customProps = {
    ...props,
    ordersQueryLoading: loading,
    orders: !error
      ? formatRetailerOrders(data?.viewer?.viewerAccount?.orders)
      : [],
    ordersQueryError: error,
    refetchOrdersOnly: refetch,
    loadMoreEntries: loadMoreRetailerOrdersFn({
      viewer: data?.viewer,
      fetchMore,
      appliedFilters,
      orderBy,
      searchFilter,
    }),
  }

  return <WrappedComponent {...customProps} />
}

const loadMoreRetailerOrdersFn =
  ({ viewer, fetchMore, appliedFilters, searchFilter, orderBy }) =>
  (pageSize) =>
    fetchMore({
      variables: {
        after: get(viewer, 'viewerAccount.orders.pageInfo.endCursor'),
        pageSize,
        appliedFilters: {
          ...appliedFiltersToGraphQLVariables(orderFilters, appliedFilters),
        },
        searchFilter,
        orderBy,
      },
      updateQuery: (_, { fetchMoreResult }) => {
        const newEdges = get(
          fetchMoreResult,
          'viewer.viewerAccount.orders.edges',
        )

        return newEdges.length
          ? {
              viewer: {
                ...viewer,
                viewerAccount: {
                  ...viewer?.viewerAccount,
                  __typename: viewer?.viewerAccount?.__typename,
                  orders: {
                    __typename: get(viewer, 'viewerAccount.orders.__typename'),
                    edges: [
                      ...get(viewer, 'viewerAccount.orders.edges', []),
                      ...newEdges,
                    ],
                    pageInfo: get(
                      fetchMoreResult,
                      `viewer.viewerAccount.orders.pageInfo`,
                    ),
                  },
                },
              },
            }
          : viewer
      },
    })

export const withRetailerOrdersTotal = (WrappedComponent) => (props) => {
  const { searchFilter, refetchOrdersOnly, appliedFilters } = props
  const {
    loading: ordersTotalsQueryLoading,
    data: orderTotalData,
    error: ordersTotalsQueryError,
    refetch,
  } = useQuery(RetailerOrdersTotalsQuery, {
    variables: {
      appliedFilters: {
        ...appliedFiltersToGraphQLVariables(orderFilters, appliedFilters),
      },
      searchFilter,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const customProps = {
    ...props,
    ordersTotalsQueryLoading,
    totalCount: orderTotalData?.viewer?.viewerAccount?.orders?.totalCount || 0,
    ordersTotalsQueryError,
    refetchOrders: () => {
      refetch()
      refetchOrdersOnly()
    },
  }

  return <WrappedComponent {...customProps} />
}

const handleSuccessMergingCriteria = (props, selectedOrdersIds) => {
  props.setSelectedOrderIds(selectedOrdersIds)
  props.setTargetOrderId('')
  props.setOrders([])
  props.handleResetSelectedOrders()
  props.navigate(MOVE_PRODUCTS_PO_SELECTION)
}

export const withCheckMergingCriteria = (WrappedComponent) => (props) => {
  const { selectedOrdersIds } = props
  const { activeToasts, removeToasts } = useNotifications()
  const [shouldRemoveToasts, setShouldRemoveToasts] = useState(false)

  useEffect(() => {
    if (shouldRemoveToasts) {
      removeToasts(activeToasts)
      setShouldRemoveToasts(false)
    }
  }, [shouldRemoveToasts])

  const [checkMergeCriteria, { data }] = useLazyQuery(MergingCriteria, {
    skip: selectedOrdersIds.length < 2 || !props.shouldCheckMergeOrders,
    variables: { selectedOrdersIds },
    client: atlasClient,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const success = data?.checkMergingOrderCriteria?.success
      const mergeOrderMessages = data?.checkMergingOrderCriteria?.message
      if (!props.shouldCheckMergeOrders) {
        return
      }
      props.toggleCheckMergeOrders(false)
      if (!success) {
        const title = 'Error'
        const description = (
          <div className={styles.moveProductsErrorToaster}>
            <span>Cannot merge selected orders because:</span>
            {mergeOrderMessages.map((message, index) => (
              <div key={index}>{message}</div>
            ))}
          </div>
        )
        const action = {
          label: 'See Help Center Article',
          handler: () => {
            window.open(HELP_LINK.pathname + HELP_LINK.search, '_blank')
          },
        }
        const timeOut = 10000
        props.renderToasters.renderErrorToast({
          description,
          title,
          action,
          timeOut,
        })
        if (!props.orderSettings?.multipleDivisionsAllowed) {
          const title = 'Warning'
          const description = (
            <div className={styles.moveProductsWarningToaster}>
              <span>Selected Orders have different divisions.</span>
              <span>
                You will be combining products from different divisions even if
                your account setting is set to “Split Orders by Division”.
              </span>
            </div>
          )
          const action = {
            label: 'See Help Center Article',
            handler: () => {
              window.open(HELP_LINK.pathname + HELP_LINK.search, '_blank')
            },
          }
          props.renderToasters.renderWarningToast({
            description,
            title,
            action,
            timeOut: 4000,
          })
        }
        return
      }

      if (!props.orderSettings?.multipleDivisionsAllowed) {
        const title = 'Warning'
        const description = (
          <div className={styles.moveProductsWarningToaster}>
            <span>Selected Orders have different divisions.</span>
            <br />
            <span>
              You will be combining products from different divisions even if
              your account setting is set to “Split Orders by Division”.
            </span>
          </div>
        )
        const action = {
          label: 'See Help Center Article',
          handler: () => {
            window.open(HELP_LINK.pathname + HELP_LINK.search, '_blank')
          },
        }
        const primaryButton = {
          children: 'Continue',
          onClick: async () => {
            await setShouldRemoveToasts(true)
            handleSuccessMergingCriteria(props, selectedOrdersIds)
          },
        }
        const secondaryButton = {
          children: 'Cancel',
          onClick: () => setShouldRemoveToasts(true),
        }
        props.renderToasters.renderWarningToast({
          description,
          title,
          shouldCloseManually: true,
          action,
          primaryButton,
          secondaryButton,
        })
        return
      }

      if (success) {
        handleSuccessMergingCriteria(props, selectedOrdersIds)
        return
      }
    },
  })
  const customProps = {
    ...props,
    selectedOrdersIds,
    checkMergeCriteria,
    success: data?.success,
    messages: data?.messages,
  }
  return <WrappedComponent {...customProps} />
}

export const withOrderSettings = (WrappedComponent) => (props) => {
  const { accountId } = props
  const brandId = toGlobalId('Brand', accountId)
  const { data } = useQuery(OrderSettings, {
    variables: {
      brandIds: [brandId],
    },
    client: atlasClient,
    skip: !accountId,
    fetchPolicy: 'no-cache',
  })

  const orderSettings = get(data, 'orderSettings[0]', {})

  const customProps = {
    ...props,
    orderSettings,
  }
  return <WrappedComponent {...customProps} />
}

export const withRetailerOrdersStaticCounts = (WrappedComponent) => (props) => {
  const {
    data,
    error: ordersStaticCountsQueryError,
    refetch: refetchStaticCounts,
  } = useQuery(RetailerOrdersStaticCounts, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const customProps = {
    ...props,
    ordersStaticCountsQueryError,
    refetchStaticCounts,
    ordersCancelled: data?.viewer?.viewerAccount?.ordersCancelled?.totalCount,
    ordersApproved: data?.viewer?.viewerAccount?.ordersApproved?.totalCount,
    ordersShipped: data?.viewer?.viewerAccount?.ordersShipped?.totalCount,
    ordersWithQuantities:
      data?.viewer?.viewerAccount?.ordersWithQuantities?.totalCount,
    ordersWithoutQuantities:
      data?.viewer?.viewerAccount?.ordersWithoutQuantities?.totalCount,
    ordersPending: data?.viewer?.viewerAccount?.ordersPending?.totalCount,
    ordersDraft: data?.viewer?.viewerAccount?.ordersDraft?.totalCount,
  }
  return <WrappedComponent {...customProps} />
}

atlas`#graphql
  query OrderExportTaskStatus($input: ListOrderExportTasksInput!) {
    listOrderExportTasks(input: $input) {
        exportTasks {
            id
            status
            downloadUrl
        }
    }
  }
`

export const withOrderExportTasks = (WrappedComponent) => (props) => {
  const { renderErrorToast, renderSuccessToast } = useRenderToasters()
  const { activeToasts, removeToasts } = useNotifications()
  const pendingToast = activeToasts.find(
    (toast) => toast.title === 'Download Pending',
  )

  const [listOrderExportTasks, { loading, stopPolling }] = useLazyQuery(
    OrderExportTaskStatusDocument,
    {
      client: atlasClient,
      pollInterval: 3000,
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (
          data.listOrderExportTasks?.exportTasks[0].status ===
          OrderExportTaskStatus.FAILURE
        ) {
          removeToasts([pendingToast])
          stopPolling()
          renderErrorToast({
            title: 'Download failed',
            description: 'An error occured with your download',
            timeOut: 5000,
          })
          return
        }
        if (
          data.listOrderExportTasks?.exportTasks[0].status ===
            OrderExportTaskStatus.SUCCESS &&
          data.listOrderExportTasks?.exportTasks[0].downloadUrl
        ) {
          removeToasts([pendingToast])
          stopPolling()
          renderSuccessToast({
            title: 'Download Successful',
            description: 'Your download has been completed successfully.',
            timeOut: 5000,
          })
          download(data.listOrderExportTasks?.exportTasks[0].downloadUrl)
        }
      },
      onError: () => {
        removeToasts([pendingToast])
        stopPolling()
        renderErrorToast({
          title: 'Download failed',
          description: 'An error occured with your download',
          timeOut: 5000,
        })
      },
    },
  )

  const customProps = {
    ...props,
    listOrderExportTasks,
    listOrderExportTasksLoading: loading || Boolean(pendingToast),
  }
  return <WrappedComponent {...customProps} />
}
